import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { identitySwitchPortal } from '@/store/identitySlice';
import { PORTAL_BRANDING } from '@/config/portalConfig';
import { portalCodeToPath } from '@/config/portalConfig';
import type { PortalCode } from '@/types/identity';
import { showApiError } from '@/utils/apiError';

export function PortalSwitcher() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector((s) => s.identity.session);
  const [switching, setSwitching] = useState(false);

  if (!session || session.availablePortals.length <= 1) {
    return null;
  }

  const uniquePortals = session.availablePortals.reduce(
    (acc, p) => {
      const key = `${p.profileId}-${p.portalCode}`;
      if (!acc.some((x) => `${x.profileId}-${x.portalCode}` === key)) {
        acc.push(p);
      }
      return acc;
    },
    [] as typeof session.availablePortals,
  );

  const handleSwitch = async (profileId: number, portalCode: PortalCode) => {
    if (profileId === session.activeProfileId && portalCode === session.activePortalCode) {
      return;
    }
    setSwitching(true);
    try {
      await dispatch(identitySwitchPortal({ profileId, portalCode })).unwrap();
      navigate(portalCodeToPath(portalCode), { replace: true });
    } catch (err) {
      showApiError(err, 'Could not switch portal');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
        Switch portal
      </label>
      <select
        disabled={switching}
        value={`${session.activeProfileId}:${session.activePortalCode}`}
        onChange={(e) => {
          const [profileId, portalCode] = e.target.value.split(':');
          void handleSwitch(Number(profileId), portalCode as PortalCode);
        }}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        {uniquePortals.map((p) => (
          <option key={`${p.profileId}-${p.portalCode}`} value={`${p.profileId}:${p.portalCode}`}>
            {PORTAL_BRANDING[p.portalCode as PortalCode]?.title ?? p.portalCode}
            {p.organizationName ? ` — ${p.organizationName}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
